/**
 * Reading a code with the camera.
 *
 * Uses the browser's own `BarcodeDetector`, which on Android Chrome is backed by
 * the same ML Kit that a native app would use — the same detector, without
 * shipping a megabyte of WebAssembly to every desktop that will never open a
 * camera. Where it is missing (Firefox, desktop Safari) scanning is simply
 * unavailable and the page falls back to typing the code, which is why the short
 * codes were made typable in the first place.
 *
 * A camera needs a secure context. Over plain HTTP on a LAN address
 * `getUserMedia` is not merely blocked, it is undefined — hence the explicit
 * check, so the failure reads as "needs HTTPS" rather than "camera broken".
 */

import { isNative } from './host';

/**
 * Whether the reader closes itself after a single code.
 *
 * True in the application, where scanning is a screen of Google's that returns
 * an answer and goes away. A caller that shows a "stop the camera" button has
 * nothing left to stop by the time `start` resolves.
 */
export const oneShot = isNative;

export interface Reading {
  value: string;
  /** As the detector reported it — `qr_code`, `ean_13`, `code_128`. It matters:
   *  the same digits under two symbologies are two different barcodes. */
  symbology: string;
}

export type ScannerFailure = 'unsupported' | 'insecure' | 'denied' | 'no_camera' | 'unknown';

interface DetectedBarcode {
  rawValue: string;
  format: string;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?(): Promise<string[]>;
}

function detectorClass(): BarcodeDetectorConstructor | null {
  const found = (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor })
    .BarcodeDetector;
  return typeof found === 'function' ? found : null;
}

export function cameraSupport(): ScannerFailure | 'ok' {
  // The application carries its own reader and asks for the camera when it
  // opens it, so there is nothing to check here and nothing to warn about.
  if (isNative) return 'ok';
  if (!window.isSecureContext) return 'insecure';
  if (!navigator.mediaDevices?.getUserMedia) return 'unsupported';
  if (!detectorClass()) return 'unsupported';
  return 'ok';
}

/**
 * Formats worth looking for: our own QR labels, what is printed on the packaging
 * a component arrives in, and — for serial numbers — DataMatrix, which is what
 * manufacturers put on the device itself when there is no room for anything
 * else. A drive, a module or an instrument almost always carries one.
 *
 * Asked for by name, but filtered against what the browser actually supports:
 * constructing the detector with a format it does not know is an error, and
 * that would take the whole camera down rather than one symbology.
 */
const WANTED = [
  'qr_code',
  'data_matrix',
  'code_128',
  'code_39',
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'itf',
  'pdf417',
];

async function usableFormats(Detector: BarcodeDetectorConstructor): Promise<string[]> {
  try {
    const supported = await Detector.getSupportedFormats?.();
    if (!supported?.length) return WANTED;
    const usable = WANTED.filter((format) => supported.includes(format));
    return usable.length ? usable : WANTED;
  } catch {
    return WANTED;
  }
}

export interface Session {
  stop(): void;
}

/**
 * Start the camera and report readings until `stop()` is called.
 *
 * The same code is reported repeatedly while it stays in frame — a camera sees
 * it thirty times a second. Repeats of the last value are suppressed here rather
 * than in every caller, because the natural reaction to an unrecognised code is a
 * dialog, and thirty of those is not a user interface.
 */
export async function start(
  video: HTMLVideoElement,
  onReading: (reading: Reading) => void,
): Promise<Session> {
  // In the application the reader is a full screen activity that covers this
  // page entirely, so the `<video>` the caller prepared is simply never seen.
  // Keeping the same signature is deliberate: eight screens open a camera, and
  // none of them has to know which one it got.
  if (isNative) {
    const { scanOnce } = await import('./scannerNative');
    await scanOnce(onReading);
    return { stop() {} };
  }

  const support = cameraSupport();
  if (support !== 'ok') throw new Error(support);

  const Detector = detectorClass();
  if (!Detector) throw new Error('unsupported');

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      // The back camera. On a phone held over a shelf the front one is pointing
      // at the ceiling.
      video: { facingMode: { ideal: 'environment' } },
      audio: false,
    });
  } catch (error) {
    const name = (error as DOMException)?.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') throw new Error('denied');
    if (name === 'NotFoundError' || name === 'OverconstrainedError') throw new Error('no_camera');
    throw new Error('unknown');
  }

  video.srcObject = stream;
  video.setAttribute('playsinline', 'true');
  await video.play().catch(() => undefined);

  const detector = new Detector({ formats: await usableFormats(Detector) });
  let running = true;
  let last = '';
  let timer = 0;

  const tick = async () => {
    if (!running) return;
    try {
      if (video.readyState >= 2) {
        const found = await detector.detect(video);
        const first = found[0];
        if (first && first.rawValue && first.rawValue !== last) {
          last = first.rawValue;
          onReading({ value: first.rawValue, symbology: first.format });
        }
      }
    } catch {
      // A frame that failed to decode is the normal case, not an error.
    }
    // Four times a second rather than every frame: detection is the expensive
    // part, and a phone that runs its camera flat out gets hot in a pocket.
    timer = window.setTimeout(() => void tick(), 250);
  };

  void tick();

  return {
    stop() {
      running = false;
      window.clearTimeout(timer);
      for (const track of stream.getTracks()) track.stop();
      video.srcObject = null;
    },
  };
}
